"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { sanitizeCmsHtml } from "@/utils/html/sanitizeCmsHtml"
import { Col, Grid, Row } from "antd"
import { useEffect, useRef, useState } from "react"


type LandingContentProps = {
  seoMetaData?: Record<string, any>
  html?: string
}

const VIEWPORT_TOP_OFFSET = 120
const SCROLL_MARGIN_TOP = 16
const FLASH_CLASS = "te-landing-content__flash"
const toRem = (px: number) => `${(px / 9).toFixed(3)}rem`

type TocItem = {
  text: string
  depth: number
  targetId?: string
  searchText?: string
  occurrence?: number
  isStrong?: boolean
  isHeading?: boolean
}

const normalizeText = (value: string) => value.replace(/\s+/g, " ").trim()

const slugify = (value: string) => {
  const normalized = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
  return normalized || "section"
}

const normalizeImageUrl = (value?: string) => {
  if (!value) return ""
  const trimmed = String(value).trim()
  if (!trimmed) return ""
  if (trimmed.startsWith("http://")) return `https://${trimmed.slice("http://".length)}`
  if (trimmed.startsWith("//")) return `https:${trimmed}`
  return trimmed
}

const buildTocFromDom = (root: HTMLElement | null) => {
  if (!root) return [] as TocItem[]

  const usedIds = new Set<string>()
  root.querySelectorAll("[id]").forEach((el) => {
    const id = (el as HTMLElement).id
    if (id) usedIds.add(id)
  })

  const makeUniqueId = (base: string) => {
    let id = slugify(base)
    if (!usedIds.has(id)) {
      usedIds.add(id)
      return id
    }
    let counter = 2
    while (usedIds.has(`${id}-${counter}`)) counter += 1
    const next = `${id}-${counter}`
    usedIds.add(next)
    return next
  }

  const tocItems: TocItem[] = []
  let currentHeading: { id: string; depth: number } | null = null
  const seenBoldByHeading = new Map<string, Set<string>>()
  const seenTextCounts = new Map<string, number>()

  const nextOccurrence = (key: string) => {
    const next = (seenTextCounts.get(key) || 0) + 1
    seenTextCounts.set(key, next)
    return next
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT)
  let node = walker.nextNode() as Element | null
  while (node) {
    const tag = node.tagName?.toLowerCase?.() || ""

    if (tag === "img") {
      const img = node as HTMLImageElement
      const src = img.getAttribute("src") || ""
      const normalizedSrc = normalizeImageUrl(src)
      if (normalizedSrc && normalizedSrc !== src) img.setAttribute("src", normalizedSrc)
      img.setAttribute("loading", img.getAttribute("loading") || "lazy")

      const existingStyle = img.getAttribute("style") || ""
      const nextStyle = `${existingStyle};max-width:100%;height:auto;`
      img.setAttribute("style", nextStyle)
    }

    if (tag === "h1" || tag === "h2" || tag === "h3") {
      const headingText = normalizeText(node.textContent || "")
      if (headingText) {
        const depth = tag === "h1" ? 0 : tag === "h2" ? 1 : 2
        const existingId = (node as HTMLElement).id
        const id = existingId ? existingId : makeUniqueId(headingText)
        ;(node as HTMLElement).id = id
        usedIds.add(id)
        ;(node as HTMLElement).style.scrollMarginTop = toRem(SCROLL_MARGIN_TOP)
        tocItems.push({
          text: headingText,
          depth,
          targetId: id,
          isHeading: true,
          occurrence: nextOccurrence(`h:${headingText}`),
        })
        currentHeading = { id, depth }
      } else {
        currentHeading = null
      }
    }

    /* //Khóa lại tính năng thêm các chữ in đậm vào mục lục ở "THông tin chi tiết"
    if (tag === "strong" || tag === "b") {
      if (!node.closest("h1,h2,h3")) {
        const boldText = normalizeText(node.textContent || "")
        if (boldText) {
          const parentId = currentHeading?.id || "__root__"
          const seen = seenBoldByHeading.get(parentId) || new Set<string>()
          if (!seen.has(boldText)) {
            seen.add(boldText)
            seenBoldByHeading.set(parentId, seen)

            const existingId = (node as HTMLElement).id
            const id = existingId ? existingId : makeUniqueId(`${parentId}-${boldText}`)
            ;(node as HTMLElement).id = id
            usedIds.add(id)
            ;(node as HTMLElement).style.scrollMarginTop = toRem(SCROLL_MARGIN_TOP)
            tocItems.push({
              text: boldText,
              depth: Math.min((currentHeading?.depth ?? 0) + 1, 3),
              targetId: id,
              isStrong: true,
              occurrence: nextOccurrence(`b:${boldText}`),
            })
          }
        }
      }
    }
    */

    node = walker.nextNode() as Element | null
  }

  return tocItems
}

const extractSeoNotes = (seoMetaData: Record<string, any> | undefined) => {
  const entries = Object.entries(seoMetaData || {})
    .map(([key, value]) => ({ key, value }))
    .filter((x) => typeof x.value === "string")
    .map((x) => ({ key: String(x.key), value: normalizeText(String(x.value)) }))
    .filter((x) => x.value)

  const notes = entries
    .map((x) => {
      const match = /^seoNote_(\d+)$/i.exec(x.key)
      if (!match) return null
      const idx = Number(match[1])
      if (Number.isNaN(idx)) return null
      return { idx, text: x.value }
    })
    .filter(Boolean) as Array<{ idx: number; text: string }>

  notes.sort((a, b) => a.idx - b.idx)
  return notes
}

const LandingContent = ({ seoMetaData = {}, html = "" }: LandingContentProps) => {
  const screens = Grid.useBreakpoint()
  const [hydrated, setHydrated] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [tocItems, setTocItems] = useState<TocItem[]>([])

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    // Render the original HTML as-is; then enhance DOM after it's in the document.
    // This avoids losing tags that DOMParser may normalize/omit.
    setTocItems([])
    const handle = window.setTimeout(() => {
      setTocItems(buildTocFromDom(contentRef.current))
    }, 0)
    return () => window.clearTimeout(handle)
  }, [html])

  const isMd = hydrated ? !!screens.md : false
  const panelMaxHeight = isMd ? `calc(100vh - ${toRem(VIEWPORT_TOP_OFFSET + 24)})` : undefined
  const sanitizedHtml = sanitizeCmsHtml(html)

  const seoNotes = extractSeoNotes(seoMetaData)
  const noteTextCounts = new Map<string, number>()
  const nextNoteOccurrence = (value: string) => {
    const key = normalizeText(value)
    const next = (noteTextCounts.get(key) || 0) + 1
    noteTextCounts.set(key, next)
    return next
  }
  const noteTocItems: TocItem[] = seoNotes.length
    ? [
        { text: "Đáng chú ý", depth: 0, isHeading: true },
        ...seoNotes.map((n) =>
          ({
            text: n.text,
            depth: 1,
            searchText: n.text,
            occurrence: nextNoteOccurrence(n.text),
          } as TocItem)
        ),
      ]
    : []

  const combinedTocItems = [...tocItems, ...noteTocItems]

  const flashElement = (element: Element | null) => {
    if (!element) return
    if (!(element instanceof HTMLElement)) return

    element.classList.remove(FLASH_CLASS)
    // restart animation
    void element.offsetWidth
    element.classList.add(FLASH_CLASS)
    window.setTimeout(() => {
      element.classList.remove(FLASH_CLASS)
    }, 1100)
  }

  const toScrollTarget = (el: Element) => {
    if (!(el instanceof HTMLElement)) return el
    const root = contentRef.current
    const withinRoot = (candidate: Element | null) => (candidate ? (root ? root.contains(candidate) : true) : false)

    // Prefer a block container for scrolling/highlighting; inline <strong>/<b>/<span> often yields awkward offsets.
    const block =
      el.closest("blockquote,h1,h2,h3,p,li") ||
      el.closest("div,section,article") ||
      null

    if (withinRoot(block)) return block as Element
    return el
  }

  const isScrollable = (el: HTMLElement) => el.scrollHeight > el.clientHeight + 1

  const findTargetById = (targetId?: string) => {
    if (!targetId) return null
    const root = contentRef.current
    if (root) {
      try {
        // Prefer searching inside our content to avoid collisions with same IDs elsewhere on the page.
        const found = root.querySelector(`#${CSS.escape(targetId)}`)
        if (found) return found
      } catch {
        // ignore invalid selectors
      }
    }
    return document.getElementById(targetId)
  }

  const scrollToTarget = (target: Element | null) => {
    if (!target) return false
    const container = scrollContainerRef.current

    const flashWhenVisible = () => {
      let tries = 0
      const maxTries = 30
      const tick = () => {
        tries += 1
        const rect = (target as HTMLElement).getBoundingClientRect()

        if (isMd && container) {
          const cRect = container.getBoundingClientRect()
          const isVisible = rect.top >= cRect.top && rect.top <= cRect.bottom
          if (isVisible || tries >= maxTries) {
            flashElement(target)
            return
          }
        } else {
          const isVisible = rect.top >= 0 && rect.top <= window.innerHeight
          if (isVisible || tries >= maxTries) {
            flashElement(target)
            return
          }
        }

        window.setTimeout(tick, 50)
      }

      window.setTimeout(tick, 80)
    }

    const targetForScroll = toScrollTarget(target)

    // When the internal container is truly scrollable, only scroll inside it.
    if (isMd && container && isScrollable(container) && container.contains(targetForScroll)) {
      const containerRect = container.getBoundingClientRect()
      const targetRect = (targetForScroll as HTMLElement).getBoundingClientRect()
      const nextTop =
        container.scrollTop + (targetRect.top - containerRect.top) - SCROLL_MARGIN_TOP
      container.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" })
      flashWhenVisible()
      return true
    }

    // Fallback (mobile / non-scrollable): default browser scrolling
    ;(targetForScroll as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" })
    flashWhenVisible()
    return true
  }

  const scrollToElementId = (targetId?: string) => {
    if (!targetId) return false
    const el = findTargetById(targetId)
    if (!el) return false
    const ok = scrollToTarget(el)
    // As a last resort, update hash so native navigation can try.
    if (!ok) {
      try {
        window.location.hash = `#${targetId}`
      } catch {
        // ignore
      }
    }
    return ok
  }

  const scrollToTextMatch = (rawText?: string, occurrence: number = 1) => {
    if (!rawText) return false
    const root = contentRef.current
    if (!root) return false

    const needle = normalizeText(rawText).toLowerCase()
    if (!needle) return false

    const safeOccurrence = Number.isFinite(occurrence) && occurrence > 0 ? Math.floor(occurrence) : 1

    const exactTargets: Element[] = []
    const includesTargets: Element[] = []
    const exactSet = new Set<Element>()
    const includesSet = new Set<Element>()

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let current = walker.nextNode() as Text | null
    while (current) {
      const hay = normalizeText(current.textContent || "").toLowerCase()
      if (hay) {
        const parent = current.parentElement
        const target = parent?.closest("blockquote,h1,h2,h3,p,li,div") || parent
        if (target) {
          const resolved = toScrollTarget(target) as Element
          if (hay === needle) {
            if (!exactSet.has(resolved)) {
              exactSet.add(resolved)
              exactTargets.push(resolved)
            }
          } else if (hay.includes(needle)) {
            if (!includesSet.has(resolved)) {
              includesSet.add(resolved)
              includesTargets.push(resolved)
            }
          }
        }
      }
      current = walker.nextNode() as Text | null
    }

    if (exactTargets.length >= safeOccurrence) {
      return scrollToTarget(exactTargets[safeOccurrence - 1])
    }

    if (includesTargets.length >= safeOccurrence) {
      return scrollToTarget(includesTargets[safeOccurrence - 1])
    }

    if (includesTargets.length) {
      return scrollToTarget(includesTargets[0])
    }

    return false
  }

  const handleTocClick = (item: TocItem) => {
    if (scrollToElementId(item.targetId)) return
    const occ = item.occurrence
    if (scrollToTextMatch(item.searchText, occ) || scrollToTextMatch(item.text, occ)) return
  }

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: toRem(12),
        border: "0.111rem solid rgba(15,23,42,0.08)",
        padding: isMd ? toRem(16) : 0,
      }}
    >
      <style>{`
        @keyframes teLandingContentFlash {
          0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); background-color: rgba(255, 193, 7, 0); }
          20% { box-shadow: 0 0 0 0.444rem rgba(255, 193, 7, 0.55); background-color: rgba(255, 193, 7, 0.22); }
          60% { box-shadow: 0 0 0 0.444rem rgba(255, 193, 7, 0.18); background-color: rgba(255, 193, 7, 0.10); }
          100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); background-color: rgba(255, 193, 7, 0); }
        }
        .${FLASH_CLASS} {
          border-radius: 0.889rem;
          animation: teLandingContentFlash 1s ease-in-out 1;
        }

        /* Scoped “HTML content” typography — content is CMS-authored via the shared TipTap editor
           (see R2g-admin src/components/RichTextEditor, also used by faq/blog/visa; content saved
           13/08 → 29/09/2026 came from Jodit) and passed through sanitizeCmsHtml before rendering.
           Rules below target plain tag semantics plus TipTap's .image-wrapper; the shared .rich-text
           class (src/styles/components/_rich-text.scss) fills in mark/code/pre/caption/empty lines.
           This also protects against global CSS resets that strip default margins. */
        .landing-content__body :where(h1,h2,h3,h4,h5,h6) {
          /* Do NOT force typography; keep only spacing so headings don't collapse under global resets. */
          margin: 1.778rem 0 1.111rem;
        }
        .landing-content__body :where(p) {
          margin: 1.111rem 0;
        }
        .landing-content__body :where(ul, ol) {
          margin: 1.111rem 0;
          padding-left: 2.444rem;
        }
        .landing-content__body :where(li) {
          margin: 0.667rem 0;
        }
        .landing-content__body :where(blockquote) {
          margin: 1.333rem 0;
          padding: 1.333rem 1.556rem;
          border-left: 0.444rem solid rgba(24,144,255,0.35);
          background: rgba(24,144,255,0.04);
          border-radius: 1.111rem;
        }
        .landing-content__body :where(blockquote p) {
          margin: 0;
        }
        .landing-content__body :where(hr) {
          border: 0;
          border-top: 0.111rem solid rgba(15,23,42,0.12);
          margin: 1.778rem 0;
        }
        /* Keep anchors readable without overriding font styles */
        .landing-content__body :where(a) {
          text-decoration: underline;
          text-underline-offset: 0.222rem;
        }
        .landing-content__body :where(img) {
          max-width: 100%;
          height: auto;
          border-radius: 1.111rem;
        }
        /* Class chung do sanitizeCmsHtml tự chèn ngay sau <img title="...">. */
        .landing-content__body :where(.cms-image-caption) {
          display: block;
          margin-top: -0.667rem;
          margin-bottom: 1.111rem;
          font-size: 1.444rem;
          color: rgba(15, 23, 42, 0.55);
          text-align: center;
          font-style: italic;
        }
        .landing-content__body :where(.image-wrapper) {
          display: block;
          width: 100%;
          margin: 1.333rem 0;
        }
        .landing-content__body :where(.image-wrapper img) {
          display: inline-block;
          vertical-align: middle;
        }
        /* Table styles for Tiptap */
        .landing-content__body :where(table) {
          border-collapse: collapse;
          margin: 1.778rem 0;
          overflow: hidden;
          table-layout: auto;
          width: 100%;
          border: 0.111rem solid rgba(15,23,42,0.12);
          border-radius: 0.889rem;
        }
        .landing-content__body :where(td, th) {
          border: 0.111rem solid rgba(15,23,42,0.12);
          box-sizing: border-box;
          min-width: 11.111rem;
          padding: 0.889rem 1.111rem;
          position: relative;
          vertical-align: top;
          word-wrap: break-word;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .landing-content__body :where(th) {
          background-color: rgba(24,144,255,0.08);
          font-weight: 600;
          text-align: left;
          color: #333;
        }
        .landing-content__body :where(thead tr) {
          background-color: rgba(246,247,251,0.8);
        }
        .landing-content__body :where(td) {
          background-color: #ffffff;
        }
        .landing-content__body :where(tr:nth-child(even) td) {
          background-color: rgba(246,247,251,0.5);
        }
        .landing-content__body :where(td > *, th > *) {
          margin: 0;
        }
        .landing-content__body :where(td[colspan], th[colspan]) {
          max-width: none;
        }
        .landing-content__body :where(td p, th p) {
          margin: 0.444rem 0;
          white-space: normal;
        }
        .landing-content__body :where(td p:first-child, th p:first-child) {
          margin-top: 0;
        }
        .landing-content__body :where(td p:last-child, th p:last-child) {
          margin-bottom: 0;
        }
        .landing-content__body :where(.tableWrapper) {
          overflow-x: auto;
          margin: 1.778rem 0;
        }
        .landing-content__body :where(.tableWrapper table) {
          margin: 0;
        }
        .landing-content__body :where(table.tiptap-table) {
          width: 100%;
        }
        /* Jodit tables get wrapped in this class by sanitizeCmsHtml (see src/utils/html/sanitizeCmsHtml.ts) */
        .landing-content__body :where(.prose__table-wrap) {
          overflow-x: auto;
          margin: 1.778rem 0;
        }
        .landing-content__body :where(.prose__table-wrap table) {
          margin: 0;
        }
        .landing-content__body :where(table colgroup col) {
          max-width: none;
        }
        .landing-content__body :where(table td p, table th p) {
          margin: 0;
        }
        @media (max-width: 768px) {
          .landing-content__body :where(table) {
            font-size: 0.875rem;
          }
          .landing-content__body :where(td, th) {
            min-width: 8.889rem;
            padding: 0.667rem;
          }
        }
      `}</style>
      <Row className="landing-content" gutter={[32, 24]}>
        <Col xs={24} md={8}>
          <div
            style={{
              backgroundColor: "#f6f7fb",
              border: "0.111rem solid #e5e7f0",
              borderRadius: "0.889rem",
              padding: "2.222rem",
              maxHeight: panelMaxHeight,
              overflowY: isMd ? "auto" : undefined,
            }}
          >
            <Text
              style={{
                marginTop: 0,
                marginBottom: "1.778rem",
                fontSize: "1.778rem",
                fontWeight: 600,
                color: "#333",
                display: "block",
              }}
            >
              Thông tin chi tiết
            </Text>
            <ul
              style={{
                paddingLeft: 0,
                margin: 0,
                listStyle: "none",
              }}
            >
              {combinedTocItems.map((item, idx) => (
                <li
                  key={`${item.text}-${idx}`}
                  style={{
                    marginBottom: item.depth <= 1 ? "1.333rem" : "0.889rem",
                    paddingLeft: toRem(12 + Math.min(item.depth, 3) * 14),
                    borderLeft:
                      item.depth <= 1 ? "0.333rem solid #1890ff" : "0.333rem solid rgba(24,144,255,0.35)",
                    transition: "all 0.3s ease",
                    cursor: item.targetId || item.searchText ? "pointer" : "default",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleTocClick(item)}
                    style={{
                      all: "unset",
                      display: "block",
                      width: "100%",
                      cursor: item.targetId || item.searchText ? "pointer" : "default",
                    }}
                  >
                    <Text
                      strong={!!(item.isStrong || item.isHeading)}
                      style={{
                        fontSize: item.depth === 0 ? "1.556rem" : "1.444rem",
                        color: "#666",
                        display: "block",
                      }}
                    >
                      {item.text}
                    </Text>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Col>

        <Col xs={24} md={16}>
          <div
            ref={scrollContainerRef}
            style={{
              maxHeight: panelMaxHeight,
              overflowY: isMd ? "auto" : undefined,
              borderRadius: toRem(10),
            }}
          >
            <div
              className="landing-content__body"
              ref={contentRef}
              style={{
                paddingRight: isMd ? toRem(10) : undefined,
                background: "#ffffff",
                borderRadius: toRem(10),
                padding: isMd ? toRem(16) : 0,
              }}
            >
              <div className="rich-text" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default LandingContent
